// @flow
import update from 'immutability-helper';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import {
  endpoints,
  getBagApi,
  postBagItemApi,
  putBagItemApi,
  deleteBagItemApi, postBagApi,
} from './api';
import { uploadImageApi, uploadImageURLApi } from '../new-order/api';
import { postItemsAutoShippingApi } from '../shopping-cart/api';
import { dataURItoBlob } from '../../utils/file';
import { setNewAlert } from '../alert/duck';
import { push } from 'react-router-redux';
import { actions as NewOrderActions } from '../new-order/duck';
import analytics from '../../analytics';
import { setVirtualBag, getVirtualBag, removeVirtualBag } from './utils/virtual-bag-storage';
import queryString from 'query-string';

import type { HTTPError } from '../../error';
import type { Bag } from '../../entities';
import type { RootState } from '../index';
import type { PostBagRequestApi, PostItemRequestApi } from './api';
import type { ShippingRequestApi, ShippingResponse, VirtualItem, VirtualItemRequestApi } from '../shopping-cart/api';
import type { ShoppingCartAction } from '../shopping-cart/duck';

export type BagAction = {
  +type: string;
  +bag?: Bag;
  +bagId?: string;
  +bagRequest?: PostBagRequestApi;
  +error?: HTTPError;
  +imageId?: string;
  +imageIds?: string[];
  +index?: number;
  +amount?: number;
  +shipping?: string;
  +virtualBag?: VirtualItem[];
  +virtualBagRequest?: VirtualItemRequestApi;
  +queryParams?: { [string]: string };
};

export type BagState = {
  +isFetchingBag?: boolean;
  +bag?: Bag;
  +fetchBagError?: HTTPError;
  +isPostingBag?: boolean;
  +postBagItemError?: HTTPError;
  +isEditingItem?: boolean;
  +editItemError?: HTTPError;
  +isDeletingItem?: boolean;
  +deleteItemError?: HTTPError;
  +virtualBag?: VirtualItem[];
};

//Actions
const VIRTUAL_BAG_FETCH_BAG_REQUEST = 'VIRTUAL_BAG_FETCH_BAG_REQUEST';
const VIRTUAL_BAG_DELETE_BAG_REQUEST = 'VIRTUAL_BAG_DELETE_BAG_REQUEST';

const VIRTUAL_BAG_POST_ITEM_REQUEST = 'VIRTUAL_BAG_POST_ITEM_REQUEST';
const VIRTUAL_BAG_POST_ITEM_SUCCESS = 'VIRTUAL_BAG_POST_ITEM_SUCCESS';
const VIRTUAL_BAG_POST_ITEM_ERROR = 'VIRTUAL_BAG_POST_ITEM_ERROR';

const VIRTUAL_BAG_EDIT_ITEM_REQUEST = 'VIRTUAL_BAG_EDIT_ITEM_REQUEST';
const VIRTUAL_BAG_EDIT_ITEM_SUCCESS = 'VIRTUAL_BAG_EDIT_ITEM_SUCCESS';
const VIRTUAL_BAG_EDIT_ITEM_ERROR = 'VIRTUAL_BAG_EDIT_ITEM_ERROR';

const VIRTUAL_BAG_DELETE_ITEM_REQUEST = 'VIRTUAL_BAG_DELETE_ITEM_REQUEST';
const VIRTUAL_BAG_DELETE_ITEM_SUCCESS = 'VIRTUAL_BAG_DELETE_ITEM_SUCCESS';
const VIRTUAL_BAG_DELETE_ITEM_ERROR = 'VIRTUAL_BAG_DELETE_ITEM_ERROR';

const VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_REQUEST = 'VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_REQUEST';
const VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_IMAGE_SUCCESS = 'VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_IMAGE_SUCCESS';
const VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_SUCCESS = 'VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_SUCCESS';
const VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_ERROR = 'VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_ERROR';

const BAG_FETCH_BAG_REQUEST = 'BAG_FETCH_BAG_REQUEST';
const BAG_FETCH_BAG_SUCCESS = 'BAG_FETCH_BAG_SUCCESS';
const BAG_FETCH_BAG_ERROR = 'BAG_FETCH_BAG_ERROR';

const BAG_POST_BAG_ITEM_REQUEST = 'BAG_POST_BAG_ITEM_REQUEST';
const BAG_POST_BAG_ITEM_IMAGE_SUCCESS = 'BAG_POST_BAG_ITEM_IMAGE_SUCCESS';
const BAG_POST_BAG_ITEM_SUCCESS = 'BAG_POST_BAG_ITEM_SUCCESS';
const BAG_POST_BAG_ITEM_ERROR = 'BAG_POST_BAG_ITEM_ERROR';

const BAG_EDIT_ITEM_REQUEST = 'BAG_EDIT_ITEM_REQUEST';
const BAG_EDIT_ITEM_SUCCESS = 'BAG_EDIT_ITEM_SUCCESS';
const BAG_EDIT_ITEM_ERROR = 'BAG_EDIT_ITEM_ERROR';

const BAG_DELETE_ITEM_REQUEST = 'BAG_DELETE_ITEM_REQUEST';
const BAG_DELETE_ITEM_SUCCESS = 'BAG_DELETE_ITEM_SUCCESS';
const BAG_DELETE_ITEM_ERROR = 'BAG_DELETE_ITEM_ERROR';

const BAG_RESET_STATE = 'BAG_RESET_STATE';

//Action creators
export function fetchVirtualBag(): BagAction {
  return {
    type: VIRTUAL_BAG_FETCH_BAG_REQUEST,
  };
}

export function deleteVirtualBag(): BagAction {
  return {
    type: VIRTUAL_BAG_DELETE_BAG_REQUEST,
  };
}

export function postVirtualBagItem(virtualBagRequest: VirtualItemRequestApi, queryParams?: { [string]: string }): BagAction {
  return {
    type: VIRTUAL_BAG_POST_ITEM_REQUEST,
    virtualBagRequest,
    queryParams,
  };
}

export function postVirtualBagItemSuccess(virtualBag: VirtualItem[], queryParams?: { [string]: string }): BagAction {
  return {
    type: VIRTUAL_BAG_POST_ITEM_SUCCESS,
    virtualBag,
    queryParams,
  };
}

export function postVirtualBagItemErrorFn(error: HTTPError): BagAction {
  return {
    type: VIRTUAL_BAG_POST_ITEM_ERROR,
    error,
  };
}

export function editVirtualBagItem(index: number, amount?: number, shipping?: string): BagAction {
  return {
    type: VIRTUAL_BAG_EDIT_ITEM_REQUEST,
    index,
    amount,
    shipping,
  };
}

export function editVirtualBagItemSuccess(virtualBag: VirtualItem[]): BagAction {
  return {
    type: VIRTUAL_BAG_EDIT_ITEM_SUCCESS,
    virtualBag,
  };
}

export function editVirtualBagItemErrorFn(error: HTTPError): BagAction {
  return {
    type: VIRTUAL_BAG_EDIT_ITEM_ERROR,
    error,
  };
}

export function deleteVirtualBagItem(index: number): ShoppingCartAction {
  return {
    type: VIRTUAL_BAG_DELETE_ITEM_REQUEST,
    index,
  };
}

export function deleteVirtualBagItemSuccess(virtualBag: VirtualItem[]): ShoppingCartAction {
  return {
    type: VIRTUAL_BAG_DELETE_ITEM_SUCCESS,
    virtualBag,
  };
}

export function deleteVirtualBagItemErrorFn(error: HTTPError): ShoppingCartAction {
  return {
    type: VIRTUAL_BAG_DELETE_ITEM_ERROR,
    error,
  };
}

export function postVirtualBagToBag(bagRequest: PostBagRequestApi): BagAction {
  return {
    type: VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_REQUEST,
    bagRequest,
  };
}

export function postVirtualBagToBagImageSuccess(imageIds: string[], bagRequest: PostBagRequestApi): BagAction {
  return {
    type: VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_IMAGE_SUCCESS,
    imageIds,
    bagRequest,
  };
}

export function postVirtualBagToBagSuccess(bag: Bag): BagAction {
  return {
    type: VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_SUCCESS,
    bag,
  };
}

export function postVirtualBagToBagErrorFn(error: HTTPError): BagAction {
  return {
    type: VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_ERROR,
    error,
  };
}

export function fetchBag(bagId: string): BagAction {
  return {
    type: BAG_FETCH_BAG_REQUEST,
    bagId,
  };
}

export function fetchBagSuccess(bag: Bag): BagAction {
  return {
    type: BAG_FETCH_BAG_SUCCESS,
    bag,
  };
}

export function fetchBagErrorFn(error: HTTPError): BagAction {
  return {
    type: BAG_FETCH_BAG_ERROR,
    error,
  };
}

export function postBagItem(bagRequest: PostBagRequestApi, bagId?: string, queryParams?: { [string]: string }): BagAction {
  return {
    type: BAG_POST_BAG_ITEM_REQUEST,
    bagRequest,
    bagId,
    queryParams,
  };
}

export function postBagItemImageSuccess(imageId: string, bagRequest: PostBagRequestApi, bagId?: string, queryParams?: { [string]: string }): BagAction {
  return {
    type: BAG_POST_BAG_ITEM_IMAGE_SUCCESS,
    imageId,
    bagRequest,
    bagId,
    queryParams,
  };
}

export function postBagItemSuccess(bag: Bag): BagAction {
  return {
    type: BAG_POST_BAG_ITEM_SUCCESS,
    bag,
  };
}

export function postBagItemErrorFn(error: HTTPError): BagAction {
  return {
    type: BAG_POST_BAG_ITEM_ERROR,
    error,
  };
}

export function editBagItem(bagId: string, itemNumber: number, amount?: number, shipping?: string): BagAction {
  return {
    type: BAG_EDIT_ITEM_REQUEST,
    itemNumber,
    bagId,
    amount,
    shipping,
  };
}

export function editBagItemSuccess(bag: Bag): BagAction {
  return {
    type: BAG_EDIT_ITEM_SUCCESS,
    bag,
  };
}

export function editBagItemErrorFn(error: HTTPError): BagAction {
  return {
    type: BAG_EDIT_ITEM_ERROR,
    error,
  };
}

export function deleteBagItem(bagId: string, itemNumber: number): BagAction {
  return {
    type: BAG_DELETE_ITEM_REQUEST,
    itemNumber,
    bagId,
  };
}

export function deleteBagItemSuccess(bag: Bag): BagAction {
  return {
    type: BAG_DELETE_ITEM_SUCCESS,
    bag,
  };
}

export function deleteBagItemErrorFn(error: HTTPError): BagAction {
  return {
    type: BAG_DELETE_ITEM_ERROR,
    error,
  };
}

export function resetBag(): BagAction {
  return {
    type: BAG_RESET_STATE,
  }
}

export const actions = {
  VIRTUAL_BAG_FETCH_BAG_REQUEST,
  VIRTUAL_BAG_DELETE_BAG_REQUEST,
  VIRTUAL_BAG_POST_ITEM_REQUEST,
  VIRTUAL_BAG_POST_ITEM_SUCCESS,
  VIRTUAL_BAG_POST_ITEM_ERROR,
  VIRTUAL_BAG_EDIT_ITEM_REQUEST,
  VIRTUAL_BAG_EDIT_ITEM_SUCCESS,
  VIRTUAL_BAG_EDIT_ITEM_ERROR,
  VIRTUAL_BAG_DELETE_ITEM_REQUEST,
  VIRTUAL_BAG_DELETE_ITEM_SUCCESS,
  VIRTUAL_BAG_DELETE_ITEM_ERROR,
  VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_REQUEST,
  VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_IMAGE_SUCCESS,
  VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_SUCCESS,
  VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_ERROR,

  BAG_FETCH_BAG_REQUEST,
  BAG_FETCH_BAG_SUCCESS,
  BAG_FETCH_BAG_ERROR,
  BAG_POST_BAG_ITEM_REQUEST,
  BAG_POST_BAG_ITEM_IMAGE_SUCCESS,
  BAG_POST_BAG_ITEM_SUCCESS,
  BAG_POST_BAG_ITEM_ERROR,
  BAG_EDIT_ITEM_REQUEST,
  BAG_EDIT_ITEM_SUCCESS,
  BAG_EDIT_ITEM_ERROR,
  BAG_DELETE_ITEM_REQUEST,
  BAG_DELETE_ITEM_SUCCESS,
  BAG_DELETE_ITEM_ERROR,
  fetchBag,
  fetchBagSuccess,
  fetchBagErrorFn,
  postBagItem,
  postBagItemImageSuccess,
  postBagItemSuccess,
  postBagItemErrorFn,
  editBagItem,
  editBagItemSuccess,
  editBagItemErrorFn,
  deleteBagItem,
  deleteBagItemSuccess,
  deleteBagItemErrorFn,

  resetBag,
  fetchVirtualBag,
  deleteVirtualBag,
  postVirtualBagItem,
  postVirtualBagItemSuccess,
  postVirtualBagItemErrorFn,
  editVirtualBagItem,
  editVirtualBagItemSuccess,
  editVirtualBagItemErrorFn,
  deleteVirtualBagItem,
  deleteVirtualBagItemSuccess,
  deleteVirtualBagItemErrorFn,
  postVirtualBagToBag,
  postVirtualBagToBagSuccess,
  postVirtualBagToBagErrorFn,
};

export const initialState: BagState = {};

// Reducer
export default function reducer(state: BagState = initialState,
                                action: BagAction): BagState {
  switch (action.type) {
    case VIRTUAL_BAG_FETCH_BAG_REQUEST:
      return update(state, {
        virtualBag: { $set: getVirtualBag() },
      });
    case VIRTUAL_BAG_DELETE_BAG_REQUEST:
      removeVirtualBag();
      return update(state, {
        $unset: ['virtualBag'],
      });
    case VIRTUAL_BAG_POST_ITEM_SUCCESS:
      if (action.virtualBag) setVirtualBag(action.virtualBag);
      return update(state, {
        $unset: ['isPostingBag'],
        virtualBag: { $set: action.virtualBag },
      });
    case VIRTUAL_BAG_EDIT_ITEM_SUCCESS:
      if (action.virtualBag) setVirtualBag(action.virtualBag);
      return update(state, {
        virtualBag: { $set: action.virtualBag },
        $unset: ['isEditingItem'],
      });
    case VIRTUAL_BAG_DELETE_ITEM_SUCCESS:
      if (action.virtualBag) setVirtualBag(action.virtualBag);
      return update(state, {
        virtualBag: { $set: action.virtualBag },
        $unset: ['isDeletingItem'],
      });

    case BAG_FETCH_BAG_REQUEST:
      return update(state, {
        isFetchingBag: { $set: true },
        $unset: ['fetchBagError'],
      });
    case BAG_FETCH_BAG_SUCCESS:
      return update(state, {
        bag: { $set: action.bag },
        $unset: ['isFetchingBag'],
      });
    case BAG_FETCH_BAG_ERROR:
      return update(state, {
        $unset: ['isFetchingBag'],
        fetchBagError: { $set: action.error },
      });
    case BAG_POST_BAG_ITEM_REQUEST:
    case VIRTUAL_BAG_POST_ITEM_REQUEST:
    case VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_REQUEST:
      return update(state, {
        isPostingBag: { $set: true },
        $unset: ['postBagItemError'],
      });
    case BAG_POST_BAG_ITEM_SUCCESS:
    case VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_SUCCESS:
      return update(state, {
        $unset: ['isPostingBag'],
        bag: { $set: action.bag },
      });
    case BAG_POST_BAG_ITEM_ERROR:
    case VIRTUAL_BAG_POST_ITEM_ERROR:
    case VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_ERROR:
      return update(state, {
        $unset: ['isPostingBag'],
        postBagItemError: { $set: action.error },
      });
    case BAG_EDIT_ITEM_REQUEST:
    case VIRTUAL_BAG_EDIT_ITEM_REQUEST:
      return update(state, {
        isEditingItem: { $set: true },
        $unset: ['editItemError'],
      });
    case BAG_EDIT_ITEM_SUCCESS:
      return update(state, {
        bag: { $set: action.bag },
        $unset: ['isEditingItem'],
      });
    case BAG_EDIT_ITEM_ERROR:
    case VIRTUAL_BAG_EDIT_ITEM_ERROR:
      return update(state, {
        $unset: ['isEditingItem'],
        editItemError: { $set: action.error },
      });
    case BAG_DELETE_ITEM_REQUEST:
    case VIRTUAL_BAG_DELETE_ITEM_REQUEST:
      return update(state, {
        isDeletingItem: { $set: true },
        $unset: ['deleteItemError'],
      });
    case BAG_DELETE_ITEM_SUCCESS:
      return update(state, {
        bag: { $set: action.bag },
        $unset: ['isDeletingItem'],
      });
    case BAG_DELETE_ITEM_ERROR:
    case VIRTUAL_BAG_DELETE_ITEM_ERROR:
      return update(state, {
        $unset: ['isDeletingItem'],
        deleteItemError: { $set: action.error },
      });
    case BAG_RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

export function fetchBagEpic(action$: Observable<BagAction>): Observable<BagAction> {
  return action$.pipe(
    ofType(BAG_FETCH_BAG_REQUEST),
    mergeMap(({ bagId }) => getBagApi(undefined, {
      route: endpoints.GET_BAG.replace(':id', bagId),
    }).pipe(
      mergeMap((bag: Bag) => of(fetchBagSuccess(bag))),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchBagErrorFn(e));
        } else if (e.code === 400) {
          return of(fetchBagErrorFn(e))
        } else if (e.code === 403) {
          return of(fetchBagErrorFn(e), push('/new-order/product'));
        }
        return of(fetchBagErrorFn(e))
      }),
    )),
  );
}

export function postBagItemImageEpic(action$: Observable<BagAction>): Observable<BagAction> {
  return action$.pipe(
    ofType(BAG_POST_BAG_ITEM_REQUEST),
    mergeMap(({ bagRequest, bagId, queryParams }) => {
      const { items } = bagRequest;
      let obs = of({});
      if (items[0].image) {
        obs = uploadImageURLApi({
          url: items[0].image,
          resourceGroup: 'PRODUCT',
        });
      } else if (items[0].imageURI) {
        const fd = new FormData();
        fd.append('file', dataURItoBlob(items[0].imageURI));
        fd.append('resourceGroup', 'PRODUCT');
        obs = uploadImageApi(fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      return obs.pipe(
        mergeMap((imageResponse: { id: string }) =>
          of(postBagItemImageSuccess(imageResponse.id, bagRequest, bagId, queryParams)),
        ),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postBagItemErrorFn(e));
        }));
    }),
  );
}

export function postBagItemEpic(action$: Observable<BagAction>, state$: StateObservable<RootState>): Observable<BagAction> {
  return action$.pipe(
    ofType(BAG_POST_BAG_ITEM_IMAGE_SUCCESS),
    mergeMap(({ imageId, bagRequest, bagId, queryParams }) => {
      const req = {
        ...bagRequest,
      };
      if (bagRequest.items[0]) {
        req.items[0].imageId = imageId;
        delete req.items[0].image;
        delete req.items[0].imageURI;
      }
      let obs = of({});
      if (!!bagId) {
        obs = postBagItemApi(req, {
          route: endpoints.POST_BAG_ITEM.replace(':bagId', bagId),
        });
      } else {
        obs = postBagApi(req);
      }
      return obs.pipe(
        mergeMap((bag: Bag) => {
          const productTrackingInfo =
            analytics.mapProduct(bag.items[0], state$.value.localValues.categoriesMap, 'item_category');
          analytics.fireCustomEvent('Product Added', {
            category: 'Product Item',
            action: 'Added',
            label: 'BAG',
            user_type: 'purchaser',
            user_referrer: window.localStorage.getItem('userReferrer') || '',
            landing_url: window.localStorage.getItem('landingUrl') || '',
            domain_country: state$.value.locale.domainCountry,
            cart_type: 'BAG',
            origin_type: req.originType,
            ...productTrackingInfo,
          }, {
            integrations: {
              'All': false,
              'Google Analytics': false,
              'Amplitude': false,
              'Facebook Pixel': true,
            },
          });
          let queryParamsString = queryString.stringify(queryParams);
          queryParamsString = queryParamsString ? `?${queryParamsString}` : '';
          return of(postBagItemSuccess(bag), push(`/new-order/checkout/bag/${bag.id}${queryParamsString}`))
        }),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            let message = 'httpErrors.post.400';
            if (typeof e.data === 'object' && e.data['name'] === 'WeightExceededError') {
              message = 'newOrder.product.bag.maxWeightExceeded';
            }
            return of(postBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, message),
            );
          } else if (e.code === 401) {
            return of(postBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.401'),
            );
          }
          return of(postBagItemErrorFn(e));
        }));
    }),
  );
}

export function editBagItemEpic(action$: Observable<BagAction>): Observable<BagAction> {
  return action$.pipe(
    ofType(BAG_EDIT_ITEM_REQUEST),
    mergeMap(({ bagId, amount, shipping, itemNumber }) => {
      const r: { [key: string]: number | string } = {};
      if (typeof amount === 'number') {
        r.amount = amount;
      }
      if (!!shipping) {
        r.shipping = shipping;
      }
      return putBagItemApi(r, {
        route: endpoints.PUT_BAG_ITEM.replace(':bagId', bagId)
          .replace(':numberId', itemNumber),
      }).pipe(
        map((bag: Bag) => editBagItemSuccess(bag)),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(editBagItemErrorFn(e));
          } else if (e.code === 400) {
            let message = 'httpErrors.post.400';
            if (typeof e.data === 'object' && e.data['name'] === 'WeightExceededError') {
              message = 'newOrder.product.bag.maxWeightExceeded';
            }
            return of(editBagItemErrorFn(e),
              setNewAlert('new-order', 'error', undefined, message),
            );
          }
          return of(editBagItemErrorFn(e))
        }),
      );
    }),
  );
}

export function deleteBagItemEpic(action$: Observable<BagAction>): Observable<BagAction> {
  return action$.pipe(
    ofType(BAG_DELETE_ITEM_REQUEST),
    mergeMap(({ bagId, itemNumber }) => {
        const req = { bagItemNumber: itemNumber };
        return deleteBagItemApi(req, {
          route: endpoints.DELETE_BAG_ITEM.replace(':bagId', bagId)
            .replace(':numberId', itemNumber),
        }).pipe(
          map((bag: Bag) => deleteBagItemSuccess(bag)),
          catchError((e: HTTPError) => {
            if (Math.round(e.code / 100) === 5) {
              return of(deleteBagItemErrorFn(e));
            } else if (e.code === 400) {
              return of(deleteBagItemErrorFn(e))
            }
            return of(deleteBagItemErrorFn(e))
          }),
        );
      },
    ),
  );
}

export function postVirtualBagItemEpic(action$: Observable<BagAction>): Observable<BagAction> {
  return action$.pipe(
    ofType(VIRTUAL_BAG_POST_ITEM_REQUEST),
    mergeMap(({ virtualBagRequest, queryParams }) =>
      postItemsAutoShippingApi(virtualBagRequest.shippingRequest).pipe(
        mergeMap((shippingResponses: ShippingResponse[]) => {
            // There might not be a virtualBag initialized
            const x = getVirtualBag() ? [...getVirtualBag()] : [];
            shippingResponses.forEach((shippingResponse: ShippingResponse) => {
              const foundIndex = x.findIndex((virtualItem: VirtualItem) =>
                virtualItem.itemId === shippingResponse.itemId);
              if (foundIndex !== -1) {
                x[foundIndex].shipping = shippingResponse.shipping;
              } else {
                x.push({
                  ...virtualBagRequest.item,
                  shipping: shippingResponse.shipping,
                  onlineStore: shippingResponse.onlineStore,
                });
              }
            });
            let queryParamsString = queryString.stringify(queryParams);
            queryParamsString = queryParamsString ? `?${queryParamsString}` : '';
            return of(postVirtualBagItemSuccess(x), push(`/new-order/quote/offline-bag${queryParamsString}`))
          },
        ),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postVirtualBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postVirtualBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postVirtualBagItemErrorFn(e));
        }),
      ),
    ),
  );
}

export function editVirtualBagItemEpic(action$: Observable<BagAction>): Observable<BagAction> {
  return action$.pipe(
    ofType(VIRTUAL_BAG_EDIT_ITEM_REQUEST),
    mergeMap(({ index, amount, shipping }) => {
      // There is a virtualBag, you are editing an item
      const virtualBag = [...getVirtualBag()];
      const items = virtualBag.map((virtualItem: VirtualItem, i: number) => {
        const shippingItemRequest: { [key: string]: any } = {
          itemId: virtualItem.itemId,
          price: virtualItem.price,
          url: virtualItem.url,
        };
        if (typeof amount === 'number' && !!amount && index === i) {
          shippingItemRequest.amount = amount;
          virtualBag[i].amount = amount;
        } else {
          shippingItemRequest.amount = virtualItem.amount;
        }
        if (typeof shipping === 'string' && !!shipping && index === i) {
          shippingItemRequest.shipping = shipping;
          shippingItemRequest.isShippingAutomatic = false;
          virtualBag[i].shipping = shipping;
          virtualBag[i].isShippingAutomatic = false;
        } else if (typeof virtualItem.isShippingAutomatic !== 'undefined') {
          shippingItemRequest.shipping = virtualItem.shipping;
          shippingItemRequest.isShippingAutomatic = virtualItem.isShippingAutomatic;
        }
        return shippingItemRequest;
      });
      const shippingRequest: ShippingRequestApi = {
        purchaseCountry: /*departureCountry.value*/'US',
        items,
      };
      return postItemsAutoShippingApi(shippingRequest).pipe(
        map((shippingResponses: ShippingResponse[]) => {
          shippingResponses.forEach((shippingResponse: ShippingResponse) => {
            const foundIndex = virtualBag.findIndex((virtualItem: VirtualItem) =>
              virtualItem.itemId === shippingResponse.itemId);
            // It should always found one
            if (foundIndex !== -1) {
              virtualBag[foundIndex].shipping = shippingResponse.shipping;
            }
          });
          return editVirtualBagItemSuccess(virtualBag);
        }),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(editVirtualBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(editVirtualBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(editVirtualBagItemErrorFn(e));
        }),
      );
    }),
  );
}

export function deleteVirtualBagItemEpic(action$: Observable<BagAction>) {
  return action$.pipe(
    ofType(VIRTUAL_BAG_DELETE_ITEM_REQUEST),
    mergeMap(({ index }) => {
      // There is a virtualBag, you are deleting an item
      const virtualBag = update(getVirtualBag(), {
        $splice: [[index, 1]],
      });
      const items = virtualBag.map((virtualItem: VirtualItem) => {
        const shippingItemRequest: { [key: string]: any } = {
          amount: virtualItem.amount,
          itemId: virtualItem.itemId,
          price: virtualItem.price,
          url: virtualItem.url,
        };
        if (typeof virtualItem.isShippingAutomatic !== 'undefined') {
          shippingItemRequest.shipping = virtualItem.shipping;
          shippingItemRequest.isShippingAutomatic = virtualItem.isShippingAutomatic;
        }
        return shippingItemRequest;
      });
      const shippingRequest: ShippingRequestApi = {
        purchaseCountry: /*departureCountry.value*/'US',
        items,
      };
      return postItemsAutoShippingApi(shippingRequest).pipe(
        map((shippingResponse: ShippingResponse[]) => {
          shippingResponse.forEach((shippingResponse: ShippingResponse) => {
            const foundIndex = virtualBag.findIndex((virtualItem: VirtualItem) =>
              virtualItem.itemId === shippingResponse.itemId);
            // It should always found one
            if (foundIndex !== -1) {
              virtualBag[foundIndex].shipping = shippingResponse.shipping;
            }
          });
          return deleteVirtualBagItemSuccess(virtualBag);
        }),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(deleteVirtualBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(deleteVirtualBagItemErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(deleteVirtualBagItemErrorFn(e));
        }),
      );
    }),
  );
}

export function postVirtualBagToBagImageEpic(action$: Observable<BagAction>): Observable<BagAction> {
  return action$.pipe(
    ofType(VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_REQUEST),
    mergeMap(({ bagRequest }) => {
      const obs = bagRequest.items.map((item: PostItemRequestApi) => {
        if (item.image) {
          return uploadImageURLApi({
            url: item.image,
            resourceGroup: 'PRODUCT',
          });
        } else if (item.imageURI) {
          const fd = new FormData();
          fd.append('file', dataURItoBlob(item.imageURI));
          fd.append('resourceGroup', 'PRODUCT');
          return uploadImageApi(fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        }
      });
      return forkJoin(obs).pipe(
        mergeMap((imagesResponse: { id: string }[]) =>
          of(postVirtualBagToBagImageSuccess(imagesResponse.map(({ id }) => id), bagRequest)),
        ),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postVirtualBagToBagErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postVirtualBagToBagErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postVirtualBagToBagErrorFn(e));
        }),
      );
    }),
  );
}

export function postVirtualBagToBagEpic(action$: Observable<BagAction>): Observable<BagAction> {
  return action$.pipe(
    ofType(VIRTUAL_BAG_POST_VIRTUAL_BAG_TO_BAG_IMAGE_SUCCESS),
    mergeMap(({ imageIds, bagRequest }) => {
      imageIds.forEach((imageId: string, n: number) => {
        bagRequest.items[n].imageId = imageId;
        delete bagRequest.items[n].image;
        delete bagRequest.items[n].imageURI;
      });
      return postBagApi(bagRequest).pipe(
        mergeMap((bag: Bag) => of(postBagItemSuccess(bag), push(`/new-order/checkout/bag/${bag.id}`))),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postVirtualBagToBagErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            let message = 'httpErrors.post.400';
            if (typeof e.data === 'object' && e.data['name'] === 'WeightExceededError') {
              message = 'newOrder.product.bag.maxWeightExceeded';
            }
            return of(postVirtualBagToBagErrorFn(e),
              setNewAlert('bag', 'error', undefined, message),
            );
          } else if (e.code === 401) {
            return of(postVirtualBagToBagErrorFn(e),
              setNewAlert('bag', 'error', undefined, 'httpErrors.post.401'),
            );
          }
          return of(postVirtualBagToBagErrorFn(e));
        }),
      );
    }),
  );
}

export function resetBagEpic(action$: Observable<BagAction>): Observable<BagAction> {
  return action$.pipe(
    ofType(NewOrderActions.NEW_ORDER_RESET_STATE),
    map(resetBag),
  );
}



// WEBPACK FOOTER //
// ./src/modules/bag/duck.js